/*
 * Native horizontal scroll rows: a flex row with overflow-x: auto, so the
 * browser does the swiping, momentum and trackpad scrolling. No carousel
 * library. Styles live in fb-scroll-row.css.
 *
 * window.FbScrollRow.bindArrows(row, prev, next)
 *   Optional arrows for mouse users. Each click scrolls most of a row width;
 *   an arrow gets the `disabled` attribute at its end of the row.
 *
 * The delivery date picker app's product rows, its upsell sliders (greeting
 * card, vase) and its link slider ("Choose a different posy"), are rendered by
 * the app, which starts Slick on them at DOMContentLoaded. This file keeps
 * Slick off them: jQuery.fn.slick is wrapped so that a Slick start on one of
 * those rows is skipped, and the row (already styled as a scroll row by
 * fb-scroll-row.css from the first paint) just gets its arrows wired. The
 * row's size never changes after load, so it adds nothing to layout shift.
 * The two block types share one markup shape (a wrapper, a title, prev/next
 * arrows, a `slick-slider-<block id>` row of `slick-slide-<block id>` cells),
 * differing only in the wrapper and arrow class prefix.
 *
 * The app still calls Slick afterwards, and gets a stand-in:
 *   - slick('getSlick') returns an object with the $slides and options its
 *     dots-width helper reads
 *   - any other method is a no-op; slick('refresh') is only called while
 *     .slick-initialized is set, which it never is here
 * The app's card click and "More details" handlers are bound to the cards,
 * which never move, and its date filter hides cards with .filtered-out.
 *
 * Every other Slick slider passes straight through the wrapper. If Slick did
 * start on an app row anyway (the wrapper installed too late), the row is
 * unslicked on its `init` event, before paint, as a fallback.
 */
(function () {
  if (window.FbScrollRow) return;

  var APP_WRAPPERS = ['.upsell-slider-wrapper', '.link-slider-wrapper'];
  var CART_SLIDER = '.cart-left-column .slick-slider-class > #upsell-slider-main-div > [class*="slick-slider-"]';
  var APP_SLIDER = APP_WRAPPERS.map(function (wrapper) {
    return wrapper + ' > [class*="slick-slider-"]';
  }).join(', ') + ', ' + CART_SLIDER;
  var APP_ARROW_PREV = ':scope > .upsell-slider-arrow--prev, :scope > .link-slider-arrow--prev';
  var APP_ARROW_NEXT = ':scope > .upsell-slider-arrow--next, :scope > .link-slider-arrow--next';

  function bindArrows(row, prev, next) {
    if (!row || (!prev && !next)) return function () {};

    function update() {
      var max = row.scrollWidth - row.clientWidth;
      if (prev) prev.disabled = row.scrollLeft <= 1;
      if (next) next.disabled = row.scrollLeft >= max - 1;
    }

    function scrollByDirection(direction) {
      row.scrollBy({ left: direction * row.clientWidth * 0.8, behavior: 'smooth' });
    }

    if (prev) prev.addEventListener('click', function (event) {
      event.preventDefault();
      scrollByDirection(-1);
    });
    if (next) next.addEventListener('click', function (event) {
      event.preventDefault();
      scrollByDirection(1);
    });
    row.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
    return update;
  }

  function isAppSlider(el) {
    return !!(el && el.nodeType === 1 && el.matches && el.matches(APP_SLIDER));
  }

  // Turns an app row into a scroll row: marks it and wires the app's arrows.
  function adoptRow(slider) {
    if (slider.__fbScrollRow) return;
    slider.__fbScrollRow = true;
    slider.classList.add('fb-scroll-row');

    var isCart = slider.matches(CART_SLIDER);
    var wrapper = isCart ? slider.closest('.slick-slider-class') : slider.parentElement;
    wrapper.classList.add('fb-app-scroll-host');
    var prev = wrapper.querySelector(isCart ? '.upsell-slider-arrows button[class*="prev-arrow-"]' : APP_ARROW_PREV);
    var next = wrapper.querySelector(isCart ? '.upsell-slider-arrows button[class*="next-arrow-"]' : APP_ARROW_NEXT);
    if (isCart) {
      if (prev) {
        prev.classList.add('fb-cart-upsell-arrow', 'fb-cart-upsell-arrow--prev');
        wrapper.appendChild(prev);
      }
      if (next) {
        next.classList.add('fb-cart-upsell-arrow', 'fb-cart-upsell-arrow--next');
        wrapper.appendChild(next);
      }
      Array.prototype.forEach.call(slider.children, function (slide, index) {
        slide.setAttribute('data-slick-index', index);
      });
    }

    var updateArrows = bindArrows(slider, prev, next);
    function placeArrows() {
      var top = slider.offsetTop + slider.offsetHeight / 2 + 'px';
      if (prev) prev.style.top = top;
      if (next) next.style.top = top;
      updateArrows();
    }
    placeArrows();
    slider.addEventListener('fb:scroll-row-updated', placeArrows);
    window.addEventListener('resize', placeArrows);
    window.addEventListener('load', placeArrows);
    // The app re-filters cards after the delivery popup closes (100ms delay,
    // then a 150ms fade), which can change the row's width.
    window.addEventListener('deliveryInfoUpdated', function () {
      setTimeout(placeArrows, 400);
    });
    Array.prototype.forEach.call(slider.querySelectorAll('img'), function (img) {
      if (!img.complete) img.addEventListener('load', placeArrows, { once: true });
    });
  }

  function standIn(slider) {
    var $ = window.jQuery;
    return {
      $slider: $(slider),
      $slides: $(slider).children(),
      options: { slidesToShow: 3, slidesToScroll: 1 },
      slideCount: slider.children.length,
      currentSlide: 0,
      unslicked: true
    };
  }

  // Wraps a Slick plugin function so that it skips the app's rows.
  function wrapSlick(original) {
    if (!original || original.__fbWrapped) return original;
    var wrapped = function (option) {
      var skip = this.filter(function () {
        // A row Slick already runs on (fallback path) stays Slick's.
        return isAppSlider(this) && (this.__fbScrollRow || !this.slick);
      });
      if (!skip.length) return original.apply(this, arguments);

      var rest = this.not(skip);
      var result = rest.length ? original.apply(rest, arguments) : undefined;
      if (typeof option === 'object' || option === undefined) {
        skip.each(function () { adoptRow(this); });
        return this;
      }
      if (option === 'getSlick') return result !== undefined ? result : standIn(skip[0]);
      if (option === 'slickGoTo') {
        var index = Number(arguments[1]);
        skip.each(function () {
          if (!this.matches(CART_SLIDER) || !this.children[index] || this.children[index].hidden) return;
          var firstVisible = Array.prototype.find.call(this.children, function (slide) { return !slide.hidden; });
          this.scrollTo({ left: this.children[index].offsetLeft - firstVisible.offsetLeft, behavior: 'smooth' });
        });
      }
      return result !== undefined && result !== rest ? result : this;
    };
    wrapped.__fbWrapped = true;
    return wrapped;
  }

  // Slick can be loaded more than once on a page (theme.liquid and
  // main-product both include it), and each load reassigns jQuery.fn.slick.
  // The setter re-wraps whichever copy lands last.
  function installWrapper() {
    var $ = window.jQuery;
    if (!$ || !$.fn) return false;
    var current = wrapSlick($.fn.slick);
    try {
      Object.defineProperty($.fn, 'slick', {
        configurable: true,
        enumerable: true,
        get: function () { return current; },
        set: function (fn) { current = wrapSlick(fn); }
      });
    } catch (error) {
      if (current) $.fn.slick = current;
    }
    return true;
  }

  // Fallback: Slick started on an app row before the wrapper existed.
  function unslickAppSlider(slider) {
    if (slider.__fbScrollRow) return;
    var $ = window.jQuery;
    if (!$ || !slider.slick || !slider.classList.contains('slick-initialized')) return;

    try {
      $(slider).slick('unslick');
    } catch (error) {
      return;
    }

    // Slick leaves its slide ids, roles and roving tabindex behind after unslick.
    Array.prototype.forEach.call(slider.children, function (slide) {
      slide.removeAttribute('role');
      slide.removeAttribute('tabindex');
      slide.removeAttribute('aria-describedby');
      if (/^slick-slide\d+$/.test(slide.id)) slide.removeAttribute('id');
      Array.prototype.forEach.call(slide.querySelectorAll('a, input, button, select'), function (el) {
        el.removeAttribute('tabindex');
      });
    });
    adoptRow(slider);
  }

  function adoptAll() {
    Array.prototype.forEach.call(document.querySelectorAll(APP_SLIDER), function (slider) {
      if (slider.classList.contains('slick-initialized')) unslickAppSlider(slider);
    });
  }

  function listenForSlickInit() {
    var $ = window.jQuery;
    if (!$) return false;
    $(document).on('init', APP_SLIDER, function (event) {
      var slider = event.currentTarget;
      // Let the app finish its own setup in this task, then unslick before
      // the browser paints.
      Promise.resolve().then(function () { unslickAppSlider(slider); });
    });
    return true;
  }

  function start() {
    installWrapper();
    listenForSlickInit();
  }

  if (window.jQuery) {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start);
  }
  window.addEventListener('load', adoptAll);

  window.FbScrollRow = { bindArrows: bindArrows, adoptAll: adoptAll };
})();
